import React, { useState, useEffect } from "react";
import "./css/sign_up.scss";
import stripe_logo from "../images/secure-stripe-payment-logo.png";

import StandardTemplate from "../templates/standard_template/standard_template";
import {
  default_account_profile,
  default_company_profile
} from "../schema/profile";
import { basic_monthly } from "../constants/subscription_products";
import UserService from "../services/user_service";
import { navigate } from "@reach/router";
import Spinner from "../components/ui/spinner";
import { handleErrorMessages } from "../services/utils";
import BasicSignupInformation from "../components/sign_up/basic_signup_information";
import PaymentPlans from "../components/sign_up/payment_plans";
import { useUser } from "../hooks/user";

export default function SignUpPage() {
  const [user, { setAccessToken }] = useUser();
  if (!!user && user.isLoggedIn) {
    navigate("/");
    return null;
  }
  const [profile, setProfile] = useState({ ...default_account_profile });
  const [companyProfile, setCompanyProfile] = useState({
    ...default_company_profile
  });
  const [selectedProduct, setSelectedProduct] = useState(basic_monthly);
  const [password, setPassword] = useState("");
  const [paymentErrors, setPaymentErrors] = useState([]);
  const [paymentName, setPaymentName] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    // If something set payment errors, ensure the spinner is hidden so that
    // the user can see the errors.
    if (paymentErrors.length > 0) {
      setShowSpinner(false);
    }
  }, [paymentErrors]);

  function onProfileChange(updatedProfile) {
    setProfile({
      ...profile,
      ...updatedProfile
    });
  }

  function onCompanyProfileChange(updatedCompanyProfile) {
    setCompanyProfile({
      ...companyProfile,
      ...updatedCompanyProfile
    });
  }

  async function onPaymentSubmit(paymentMethod) {
    setShowSpinner(true);
    setPaymentErrors([]);

    // Returning customer? If this email already has an account, don't create a
    // duplicate (or double-charge). Route them to log in — an inactive account
    // reactivates from there at the returning rate, with all files preserved.
    const status = await UserService.getEmailStatus(profile.email);
    if (status && status.exists) {
      setPaymentErrors([
        status.active
          ? "You already have an active account with this email. Please log in instead."
          : "You already have an account with this email. Please log in — you can reactivate from there and all your files will still be here."
      ]);
      setShowSpinner(false);
      return;
    }

    await UserService.postRegistration(
      { ...profile },
      password,
      { ...companyProfile },
      selectedProduct,
      paymentName,
      profile.email,
      profile.cell_phone,
      promoCode,
      paymentMethod
    )
      .then((response) => {
        setAccessToken(response.access_token);
        navigate("/sign-up/receipt");
      })
      .catch(error => handleErrorMessages(error, setPaymentErrors));
    setShowSpinner(false);
  }

  return (
    <StandardTemplate className="sign-up">
      {showSpinner && <Spinner />}
      <div className="main-title">Subscribe</div>
      <div className="basic-info-section">
        <PaymentPlans
          product={selectedProduct}
          setProduct={setSelectedProduct}
        />
        <BasicSignupInformation
          profile={profile}
          onProfileChange={onProfileChange}
          companyProfile={companyProfile}
          onCompanyProfileChange={onCompanyProfileChange}
          password={password}
          onPasswordChange={value => setPassword(value.password)}
          onBeforeCreatePaymentMethod={() => setShowSpinner(true)}
          paymentName={paymentName}
          setPaymentName={setPaymentName}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          onSubmit={onPaymentSubmit}
          paymentErrors={paymentErrors}
          setPaymentErrors={setPaymentErrors}
        />
      </div>

      {/* VIEW / VISION are set up with Sales (annual commitment + org chart), so
          a visitor who lands here instead of calling sees the products and how to
          reach Sales. */}
      <div
        className="dashboard-sales-section"
        style={{
          maxWidth: 900,
          margin: "28px auto 0",
          background: "#e7f0f8",
          border: "1px solid #d7e0e8",
          borderRadius: 14,
          padding: "22px 24px"
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4 }}>
          Management Dashboards
        </div>
        <div style={{ color: "#5a6b7a", fontSize: 14, marginBottom: 14 }}>
          Add a management dashboard to roll up production across your team. These
          are set up with our Sales team (your org chart gets you live).
        </div>

        {[
          {
            name: "VIEW — Retail Management Dashboard + LO Dashboard"
          },
          {
            name: "VISION — Wholesale Management Dashboard + AE Dashboard"
          },
          {
            name: "VIEW + VISION — Retail & Wholesale bundle"
          }
        ].map(item => (
          <div
            key={item.name}
            style={{
              background: "#ffffff",
              border: "1px solid #d7e0e8",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 10
            }}
          >
            <div style={{ fontWeight: 700, color: "#0c5a9e", fontSize: 15 }}>
              {item.name}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: "#5a6b7a",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                marginTop: 6
              }}
            >
              Annual commitment &middot; 2-year commitment also available
            </div>
          </div>
        ))}

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            background: "#ffffff",
            border: "1px solid #0c5a9e",
            borderLeft: "5px solid #0c5a9e",
            borderRadius: 10,
            padding: "14px 18px"
          }}
        >
          <span style={{ fontWeight: 700, color: "#0c5a9e", fontSize: 15 }}>
            Contact Sales at:
          </span>
          <span style={{ fontWeight: 700, color: "#0c5a9e", fontSize: 15 }}>
            (805) 498-4843 &middot; sales@golanis.com
          </span>
        </div>
      </div>

      <div className="stripe-logo">
        <picture>
          <source srcset={stripe_logo} type="image/png" />
          <img src={stripe_logo} type="image/png" alt="Stripe logo" />
        </picture>
      </div>
    </StandardTemplate>
  );
}
